/**
 * sanitize.js — defensive helpers for localStorage XSS hardening.
 *
 * React's JSX escapes text content automatically, so the primary attack
 * surface in this app is:
 *   1. Names/IDs that flow into URLs, WhatsApp share text, or `href`s.
 *   2. JSON blobs in localStorage that other tabs / extensions can tamper
 *      with, then get parsed and trusted by sync/backend code paths.
 *
 * These helpers are a thin defensive layer: strip control chars, drop
 * tag-like substrings, cap length, and validate shape before parse.
 */

const MAX_NAME_LEN = 60;
const MAX_TEXT_LEN = 500;
const MAX_JSON_BYTES = 2 * 1024 * 1024; // 2 MB cap

/**
 * Sanitize a free-form name (player names, side labels, club names).
 *  - Strips <, >, & quote chars to neutralize naive HTML injection.
 *  - Strips control characters (except space, tab).
 *  - Collapses whitespace and caps length.
 */
export const sanitizeName = (raw, maxLen = MAX_NAME_LEN) => {
  if (raw == null) return '';
  const s = String(raw)
    .replace(/[<>'"`]/g, '')        // neutralize HTML/quote chars
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '') // strip control chars
    .replace(/\s+/g, ' ')           // collapse whitespace
    .trim();
  return s.slice(0, maxLen);
};

/**
 * Sanitize longer free-form text (e.g. descriptions, share messages).
 * Same rules but with a higher length cap.
 */
export const sanitizeText = (raw, maxLen = MAX_TEXT_LEN) => sanitizeName(raw, maxLen);

/**
 * Safely parse a JSON string from localStorage. Returns `fallback` if:
 *  - the string is missing, too big, malformed, or
 *  - the parsed value doesn't match the expected shape ('array' | 'object').
 */
export const safeParseJSON = (raw, expected = 'object', fallback = null) => {
  if (raw == null) return fallback;
  if (typeof raw !== 'string') return fallback;
  if (raw.length > MAX_JSON_BYTES) return fallback;
  try {
    const v = JSON.parse(raw);
    if (expected === 'array') return Array.isArray(v) ? v : fallback;
    if (expected === 'object') return v && typeof v === 'object' && !Array.isArray(v) ? v : fallback;
    return v;
  } catch { return fallback; }
};

/**
 * Coerce a value to a finite number within bounds, otherwise return fallback.
 */
export const sanitizeNumber = (v, { min = -Infinity, max = Infinity, fallback = 0 } = {}) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  if (n < min || n > max) return fallback;
  return n;
};

/**
 * Validate and lightly sanitize a match record before storing/syncing.
 * Returns a cleaned object; never throws.
 */
export const sanitizeMatchRecord = (m) => {
  if (!m || typeof m !== 'object') return null;
  return {
    id: sanitizeName(m.id, 120),
    sideA: sanitizeName(m.sideA),
    sideB: sanitizeName(m.sideB),
    scoreA: sanitizeNumber(m.scoreA, { min: 0, max: 9999 }),
    scoreB: sanitizeNumber(m.scoreB, { min: 0, max: 9999 }),
    winner: m.winner === 'A' || m.winner === 'B' ? m.winner : null,
    completedAt: sanitizeName(m.completedAt, 40),
    games: Array.isArray(m.games) ? m.games.slice(0, 7).map(g => ({
      scoreA: sanitizeNumber(g?.scoreA, { min: 0, max: 9999 }),
      scoreB: sanitizeNumber(g?.scoreB, { min: 0, max: 9999 }),
      winner: g?.winner === 'A' || g?.winner === 'B' ? g.winner : null,
    })) : [],
    durationSec: sanitizeNumber(m.durationSec, { min: 0, max: 60 * 60 * 24 }),
    preset: sanitizeName(m.preset, 40),
  };
};
